import { useState } from "react";
import { translateRouteModesStream } from "@services/ai/aiApi";
import {
  buildRoutePointTranslationData,
  prepareTranslatedPointUpdates,
} from "@utils/mode_tool/routePointTranslation";
import { isDirectionPoint } from "@utils/mode_tool/routePointAutomation";

function useRoutePointTranslation({
  tourId,
  modePoints,
  onCommitPoint,
  onTranslationApplied,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [pointScope, setPointScope] = useState("all");
  const [targetPoint, setTargetPoint] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [failedPoints, setFailedPoints] = useState([]);
  const [translatedPoints, setTranslatedPoints] = useState([]);
  const [streamProgress, setStreamProgress] = useState({ total: 0, completed: 0, points: [], done: false, completeReceived: false });

  function open(point = null) {
    setTargetPoint(point);
    setSelectedLanguage("");
    setError("");
    setSuccess("");
    setFailedPoints([]);
    setTranslatedPoints([]);
    setStreamProgress({ total: 0, completed: 0, points: [], done: false, completeReceived: false });
    setIsOpen(true);
  }

  function close() {
    if (!isTranslating) setIsOpen(false);
  }

  async function translate() {
    const language = String(selectedLanguage || "").toLowerCase().trim();
    const points = Array.isArray(modePoints) ? modePoints : [];
    const sourcePoints = targetPoint ? [targetPoint] : points;
    const targetPoints = sourcePoints.filter((point) => targetPoint || pointScope === "all"
      || (pointScope === "directions"
      ? isDirectionPoint(point)
      : !isDirectionPoint(point)));
    if (!language || language === "en" || language.length < 2 || language.length > 10) {
      setError("Select a language other than EN.");
      return;
    }
    if (!targetPoints.length || targetPoints.length > 500) {
      setError("Select a route with between 1 and 500 points.");
      return;
    }
    if (!String(tourId || "").trim()) {
      setError("The tour ID is unavailable. Select a tour before translating.");
      return;
    }
    const data = buildRoutePointTranslationData(targetPoints);
    if (data.some((row) => !row.id || row.id.length > 160) ||
        new Set(data.map((row) => row.id)).size !== data.length) {
      setError("Route points must have unique IDs of up to 160 characters.");
      return;
    }
    if (typeof onCommitPoint !== "function") {
      setError("Route points cannot be updated right now.");
      return;
    }

    setIsTranslating(true);
    setError("");
    setSuccess("");
    setFailedPoints([]);
    setTranslatedPoints([]);
    const pointsById = new Map(targetPoints.map((point) => [String(point?.id || ""), point]));
    const appliedIds = new Set();
    const failedById = new Map();
    setStreamProgress({
      total: data.length,
      completed: 0,
      done: false,
      completeReceived: false,
      points: targetPoints.map((point, index) => ({
        index,
        id: String(point.id),
        point,
        label: getPointLabel(point, String(point.id)),
        status: "pending",
      })),
    });

    async function handlePointEvent(eventData) {
      const row = eventData?.point ?? eventData?.data ?? eventData;
      const id = String(eventData?.id || row?.id || "");
      const point = pointsById.get(id);
      const eventLanguage = String(eventData?.language || row?.language || language).toLowerCase();
      let status = String(eventData?.status || (row?.error === true ? "error" : "completed"));
      let translatedName = "";
      let message = String(eventData?.message || row?.error_message || "");

      if (eventLanguage !== language) {
        status = "error";
        message = "The translation response has an unexpected language.";
      } else if (status === "error" || row?.error === true || !point) {
        status = "error";
        message = message || (point ? "This point could not be translated." : "Unknown point returned by the translation API.");
        failedById.set(id || "unknown", {
          id: id || "unknown",
          label: getPointLabel(point, id || "Unknown point"),
        });
      } else if (!appliedIds.has(id)) {
        try {
          const [update] = prepareTranslatedPointUpdates([point], [row], language);
          onCommitPoint(update.id, update.localized);
          if (!appliedIds.size) onTranslationApplied?.(language);
          appliedIds.add(id);
          translatedName = String(row?.name || "");
          setTranslatedPoints((current) => current.some((item) => item.id === id)
            ? current
            : [...current, { id, label: getPointLabel(point, id) }]);
        } catch (pointError) {
          status = "error";
          message = pointError?.message || "The translation for this point is incomplete.";
          failedById.set(id, { id, label: getPointLabel(point, id) });
        }
      }

      setStreamProgress((current) => {
        const nextPoints = [...current.points];
        const index = nextPoints.findIndex((item) => item.id === id);
        if (index < 0) return current;
        nextPoints[index] = {
          ...nextPoints[index],
          point: row || nextPoints[index].point,
          status,
          translatedName,
          message,
        };
        return {
          ...current,
          points: nextPoints,
          completed: nextPoints.filter((item) => item.status !== "pending").length,
          total: Number(eventData?.total) || current.total,
        };
      });
      if (status === "error") {
        setFailedPoints(Array.from(failedById.values()));
      }
    }

    try {
      const response = await translateRouteModesStream({
        tour_id: String(tourId),
        language,
        data,
      }, async ({ event, data: eventData }) => {
        if (event === "start") {
          setStreamProgress((current) => ({ ...current, total: Number(eventData?.total) || current.total }));
        } else if (event === "point") {
          await handlePointEvent(eventData);
        } else if (event === "complete") {
          setStreamProgress((current) => ({ ...current, done: true, completeReceived: true }));
        }
      });
      if ((response?.error && !response?.id) || response?.success === false) {
        throw new Error(response?.message || "Could not translate the route points.");
      }
      const result = response?.body ?? response?.data ?? response;
      if (Array.isArray(result?.language) && !result.language.includes(language)) {
        throw new Error("The translation response has an unexpected language.");
      }
      const translatedRows = Array.isArray(result?.data)
        ? result.data
        : result?.data && typeof result.data === "object"
          ? [result.data]
          : result?.id
            ? [result]
            : null;
      if (!translatedRows) {
        throw new Error("The translation response does not contain a points list.");
      }
      for (const row of translatedRows) {
        if (String(row?.language || language).toLowerCase() !== language) continue;
        if (row?.error === true) {
          failedById.set(String(row?.id || "unknown"), {
            id: String(row?.id || "unknown"),
            label: getPointLabel(pointsById.get(String(row?.id || "")), String(row?.id || "Unknown point")),
          });
          continue;
        }
        if (!appliedIds.has(String(row?.id || ""))) {
          await handlePointEvent({ id: row?.id, language, point: row, status: "completed" });
        }
      }
      setFailedPoints(Array.from(failedById.values()));
      setStreamProgress((current) => ({ ...current, completed: current.total, done: true, completeReceived: true }));
      setSuccess(
        appliedIds.size
          ? `${appliedIds.size} points translated into ${language.toUpperCase()}. Review them and save the route.`
          : "No points were translated successfully.",
      );
    } catch (requestError) {
      setError(requestError?.message || "Could not translate the route points.");
      setStreamProgress((current) => ({ ...current, done: true }));
    } finally {
      setIsTranslating(false);
    }
  }

  return {
    isOpen,
    open,
    openForPoint: (point) => open(point),
    isSinglePoint: Boolean(targetPoint),
    close,
    selectedLanguage,
    setSelectedLanguage,
    pointScope,
    setPointScope,
    targetPointCount: targetPoint ? 1 : (Array.isArray(modePoints) ? modePoints : []).filter((point) => pointScope === "all"
      || (pointScope === "directions"
        ? isDirectionPoint(point)
        : !isDirectionPoint(point))).length,
    isTranslating,
    error,
    success,
    failedPoints,
    translatedPoints,
    streamProgress,
    translate,
  };
}

function readPointName(point) {
  const name = point?.name?.en;
  if (typeof name === "string") return name;
  return String(name?.value || "").trim();
}

function getPointLabel(point, fallbackId) {
  if (!point) return fallbackId;
  const pointName = readPointName(point);
  return `#${point.number_point ?? point.labelCode ?? "?"}${pointName ? ` — ${pointName}` : ` (${fallbackId})`}`;
}

export default useRoutePointTranslation;
